import React, { useState } from "react";
import { DiaNoDisponible } from "./DiasNoDisponiblesHabitacionHotel";
import Swal from "sweetalert2";
import { fetchAxiosToken } from "../../../helpers/fetchAxiosToken";
import ResponsiveModalAbsolute from "../../../components/ResponsiveModalAbsolute";
import BotonInformacion from "../../../components/helpers/BotonInformacion";
import { InputDate } from "../../../components/InputDate";

const DAYS = [
    "Lunes",
    "Martes",
    "Miercoles",
    "Jueves",
    "Viernes",
    "Sabado",
    "Domingo",
];

interface ModalProps {
    modalVisible: boolean;
    setModalVisible: (visible: boolean) => void;
    getAllDias: () => void;
    id_casa: number;
}

type errorObject = { [key: string]: string };

export const ModalCrearDia = ({
    modalVisible,
    setModalVisible,
    getAllDias,
    id_casa,
}: ModalProps) => {
    const [loading, setLoading] = useState(false);
    const [input, setInput] = useState<DiaNoDisponible>({
        tipo: "dia",
        fecha_inicio: "",
        fecha_final: "",
        dia_inicio: "Lunes",
        dia_final: "Lunes",
        motivo: "Alquilado",
        otro_motivo: "",
    });
    const [errors, setErrors] = useState<DiaNoDisponible>({});
    const [pasos, setPasos] = useState(0);

    // --- FUNCIÓN HELPER PARA FORMATEAR LA FECHA ---
    // Convierte "2026-1-1" o Objetos Date a "2026-01-01"
    const formatearFecha = (fecha: any) => {
        if (!fecha) return "";

        // Si viene como string con guiones (ej: "2026-1-1")
        if (typeof fecha === "string" && fecha.includes("-")) {
            const parts = fecha.split("-");
            const year = parts[0];
            const month = parts[1].padStart(2, "0"); // Agrega el 0 si falta
            const day = parts[2].padStart(2, "0"); // Agrega el 0 si falta
            return `${year}-${month}-${day}`;
        }

        // Si viene como objeto Date
        const d = new Date(fecha);
        if (!isNaN(d.getTime())) {
            const year = d.getFullYear();
            const month = (d.getMonth() + 1).toString().padStart(2, "0");
            const day = d.getDate().toString().padStart(2, "0");
            return `${year}-${month}-${day}`;
        }

        return fecha; // Retorna original si no se pudo formatear
    };

    const handleInputChange = (e: React.ChangeEvent<any>) => {
        setInput({
            ...input,
            [e.target.name]: e.target.value,
        });
    };

    const validateInput = (form: DiaNoDisponible) => {
        let errores: errorObject = {};
        if (pasos === 1) {
            if (input.tipo === "dia") {
                if (!form.dia_inicio) {
                    errores.dia_inicio = "Debe ingresar un día válido";
                } else if (!form.dia_final) {
                    errores.dia_final = "Debe ingresar un día válido";
                }
            }
            if (!form.fecha_inicio) {
                errores.fecha_inicio = "Debe ingresar una fecha válida";
            } else if (!form.fecha_final) {
                errores.fecha_final = "Debe ingresar una fecha válida";
            }
        }
        return errores;
    };

    const handleSubmit = async () => {
        const errors = validateInput(input);
        setErrors(errors);

        if (Object.entries(errors).length === 0) {
            if (pasos === 0) {
                setPasos(1);
            } else if (pasos === 1) {
                setPasos(2);
            } else if (pasos === 2) {
                try {
                    setLoading(true);

                    // AQUI APLICAMOS EL FORMATEO ANTES DE ENVIAR
                    const payload = {
                        id_habitacion: id_casa,
                        ...input,
                        fecha_inicio: formatearFecha(input.fecha_inicio),
                        fecha_final: formatearFecha(input.fecha_final),
                    };

                    const data = await fetchAxiosToken({
                        url: `/disponibilidades_habitaciones_hoteles`,
                        body: payload, // Usamos el payload formateado
                        method: "post",
                    });

                    setLoading(false);
                    setModalVisible(false);
                    setPasos(0);
                    setInput({
                        tipo: "dia",
                        fecha_final: "",
                        fecha_inicio: "",
                        dia_final: "",
                        dia_inicio: "",
                        motivo: "",
                        otro_motivo: "",
                    });
                    setErrors({});
                    getAllDias();
                    Swal.fire({
                        confirmButtonColor: "#029BFC",
                        iconColor: "#029BFC",
                        title: "Actualizado con exito",
                        icon: "success",
                        confirmButtonText: "Ok",
                    });
                } catch (error) {
                    console.log(error);
                    setLoading(false); // Aseguramos quitar loading si falla
                }
            }
        }
    };

    return (
        <ResponsiveModalAbsolute
            modalVisible={modalVisible}
            setModalVisible={setModalVisible}
            onClose={() => {
                setErrors({});
                setInput({
                    tipo: "dia",
                    fecha_final: "",
                    fecha_inicio: "",
                    dia_final: "",
                    dia_inicio: "",
                    motivo: "",
                    otro_motivo: "",
                });
            }}
        >
            <div className="flex flex-col items-center gap-2 p-3 2xl:p-6">
                <div className="relative flex items-center justify-center">
                    <div className="mr-1 text-base font-medium 2xl:text-lg">
                        Añadir días no disponibles
                    </div>
                    <BotonInformacion text="Ingresa los días de la semana o fechas en la cual tu casa no estara disponible" />
                </div>

                {pasos === 0 && (
                    <>
                        <label className="flex w-full max-w-xs ml-2 text-sm font-medium">
                            Elige si deseas bloquear por días de la semana o por
                            fechas
                        </label>
                        <select
                            value={input.tipo}
                            name="tipo"
                            onChange={handleInputChange}
                            className="w-full max-w-xs p-2 border rounded-lg shadow-md 2xl:p-3 placeholder:italic"
                        >
                            <option value={"dia"}>Días de la semana</option>
                            <option value={"fecha"}>Fechas especiales</option>
                        </select>
                    </>
                )}

                {pasos === 1 && (
                    <>
                        <div
                            onClick={() => setPasos(0)}
                            className={`text-black flex w-full max-w-xs py-5 items-center 2xl:text-base text-sm  cursor-pointer hover:underline hover:scale-105`}
                        >
                            <svg
                                className="w-5 h-5 mt-0.5 text-black"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                            <span className="mx-2">Regresar</span>
                        </div>

                        {input.tipo === "dia" ? (
                            <label className="flex w-full max-w-xs mt-3 ml-2 text-sm font-medium text-blue-500">
                                Primero selecciona un rango de fechas en la que
                                desees bloquear los días de la semana
                            </label>
                        ) : (
                            <label className="flex w-full max-w-xs mt-3 ml-2 text-sm font-medium text-blue-500">
                                Selecciona en que rango de fechas deseas
                                bloquear tu calendario
                            </label>
                        )}

                        <label className="flex w-full max-w-xs mt-2 ml-2 text-sm font-medium">
                            Fecha inicial
                        </label>
                        <InputDate
                            selectedDay={input.fecha_inicio}
                            onChange={(date) =>
                                setInput({
                                    ...input,
                                    fecha_inicio: date,
                                })
                            }
                            className="flex max-w-xs p-2 mx-auto border rounded-lg shadow-md 2xl:p-3 placeholder:italic"
                            placeholderText="21/02/2023"
                        />
                        {errors.fecha_inicio && (
                            <p className="my-1 text-sm font-bold text-red-500 text-md">
                                {errors.fecha_inicio}
                            </p>
                        )}
                    </>
                )}

                {pasos === 1 && (
                    <>
                        <label className="flex w-full max-w-xs ml-2 text-sm font-medium">
                            Fecha final
                        </label>
                        <InputDate
                            selectedDay={input.fecha_final}
                            onChange={(date) =>
                                setInput({
                                    ...input,
                                    fecha_final: date,
                                })
                            }
                            className="flex max-w-xs p-2 mx-auto border rounded-lg shadow-md 2xl:p-3 placeholder:italic"
                            placeholderText="21/02/2023"
                        />
                        {errors.fecha_final && (
                            <p className="my-1 text-sm font-bold text-red-500 text-md">
                                {errors.fecha_final}
                            </p>
                        )}
                    </>
                )}

                {pasos === 1 && input.tipo === "dia" && (
                    <>
                        <label className="flex w-full max-w-xs mt-3 ml-2 text-sm font-medium text-blue-500">
                            Elige los días de la semana deseas bloquear tu
                            calendario
                        </label>
                        <label className="flex w-full max-w-xs mt-2 ml-2 text-sm font-medium">
                            Día de la semana inicio
                        </label>
                        <select
                            value={input.dia_inicio}
                            name="dia_inicio"
                            onChange={handleInputChange}
                            className="w-full max-w-xs p-2 border rounded-lg shadow-md 2xl:p-3 placeholder:italic"
                        >
                            {DAYS.map((day) => (
                                <option key={day} value={day}>
                                    {day}
                                </option>
                            ))}
                        </select>
                        {errors.dia_inicio && (
                            <p className="my-1 text-sm font-bold text-red-500 text-md">
                                {errors.dia_inicio}
                            </p>
                        )}
                    </>
                )}

                {pasos === 1 && input.tipo === "dia" && (
                    <>
                        <label className="flex w-full max-w-xs ml-2 text-sm font-medium">
                            Día de la semana final
                        </label>
                        <select
                            value={input.dia_final}
                            name="dia_final"
                            onChange={handleInputChange}
                            className="w-full max-w-xs p-2 border rounded-lg shadow-md 2xl:p-3 placeholder:italic"
                        >
                            {DAYS.map((day) => (
                                <option key={day} value={day}>
                                    {day}
                                </option>
                            ))}
                        </select>
                        {errors.dia_final && (
                            <p className="my-1 text-sm font-bold text-red-500 text-md">
                                {errors.dia_final}
                            </p>
                        )}
                    </>
                )}

                {pasos === 2 && (
                    <>
                        <div
                            onClick={() => setPasos(1)}
                            className={`text-black flex w-full max-w-xs py-5 items-center 2xl:text-base text-sm  cursor-pointer hover:underline hover:scale-105`}
                        >
                            <svg
                                className="w-5 h-5 mt-0.5 text-black"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                            <span className="mx-2">Regresar</span>
                        </div>
                        <label className="flex w-full max-w-xs mt-3 ml-2 text-sm font-medium">
                            Motivo
                        </label>
                        <select
                            value={input.motivo}
                            name="motivo"
                            onChange={handleInputChange}
                            className="w-full max-w-xs p-2 border rounded-lg shadow-md 2xl:p-3 placeholder:italic"
                        >
                            <option value={"Bluecorner"}>Bluecorner</option>
                            <option value={"Airbnb"}>Airbnb</option>
                            <option value={"Booking.com"}>Booking.com</option>
                            <option value={"Alquilado"}>Alquilado</option>
                            <option value={"En mantenimiento"}>
                                En mantenimiento
                            </option>
                            <option value={"Evento privado"}>
                                Evento privado
                            </option>
                            <option value={"Otro"}>Otro</option>
                        </select>
                    </>
                )}
                {pasos === 2 && input.motivo === "Otro" && (
                    <>
                        <label className="flex w-full max-w-xs ml-2 text-sm font-medium">
                            Detalle
                        </label>
                        <input
                            value={input.otro_motivo}
                            onChange={handleInputChange}
                            className="w-full max-w-xs p-2 border rounded-lg shadow-md 2xl:p-3 placeholder:italic"
                            type="text"
                            name="otro_motivo"
                        />
                    </>
                )}

                <button
                    disabled={loading}
                    onClick={() => handleSubmit()}
                    className="p-3 mt-6 font-medium 2xl:text-base text-sm text-white border rounded-lg bg-[#1C4CBB] active:translate-y-1 disabled:translate-y-0 disabled:from-slate-400 disabled:to-gray-400 disabled:cursor-default"
                >
                    {Number(pasos) > 1
                        ? "Añadir día no disponible"
                        : "Siguiente"}
                </button>
            </div>
        </ResponsiveModalAbsolute>
    );
};
